import { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';

export default function ContactForm() {
  const [status, setStatus] = useState(null);
  const { t } = useLanguage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');

    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setStatus('success');
        e.target.reset();
      } else {
        setStatus('error');
      }
    } catch (error) {
      setStatus('error');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Honeypot - Hidden from humans */}
      <div className="sr-only absolute -z-10 opacity-0 pointer-events-none" aria-hidden="true">
        <input type="text" name="website_url" tabIndex="-1" autoComplete="off" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
            {t({ da: 'Navn', en: 'Name' })}
          </label>
          <input
            type="text"
            id="name"
            name="name"
            required
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all outline-none"
            placeholder={t({ da: 'Dit navn', en: 'Your name' })}
          />
        </div>
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
            {t({ da: 'Telefonnummer', en: 'Phone number' })}
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            required
            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all outline-none"
            placeholder="+45"
          />
        </div>
      </div>

      <div>
        <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
          {t({ da: 'Adresse', en: 'Address' })}
        </label>
        <input
          type="text"
          id="address"
          name="address"
          required
          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all outline-none"
          placeholder={t({ da: 'Vejnavn, nr., postnr. og by', en: 'Street, no., zip and city' })}
        />
      </div>

      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
        <input
          type="email"
          id="email"
          name="email"
          required
          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all outline-none"
          placeholder="din@email.dk"
        />
      </div>

      <div>
        <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-1">
          {t({ da: 'Vælg ønsket service', en: 'Select desired service' })}
        </label>
        <select
          id="service"
          name="service"
          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all outline-none bg-white"
          defaultValue=""
          required
        >
          <option value="" disabled>{t({ da: 'Vælg en service...', en: 'Select a service...' })}</option>
          <option value="erhvervsrengoring">{t({ da: 'Erhvervsrengøring', en: 'Business cleaning' })}</option>
          <option value="privatrengoring">{t({ da: 'Privatrengøring', en: 'Private cleaning' })}</option>
          <option value="hovedrengoring">{t({ da: 'Hovedrengøring', en: 'Deep cleaning' })}</option>
          <option value="flytterengoring">{t({ da: 'Flytterengøring', en: 'Move-out cleaning' })}</option>
          <option value="vinduespudsning">{t({ da: 'Vinduespudsning', en: 'Window cleaning' })}</option>
          <option value="andet">{t({ da: 'Andet', en: 'Other' })}</option>
        </select>
      </div>

      <div>
        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
          {t({ da: 'Besked', en: 'Message' })}
        </label>
        <textarea
          id="message"
          name="message"
          rows="4"
          required
          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary transition-all outline-none resize-none"
          placeholder={t({ da: 'Fortæl os lidt om dine ønsker...', en: 'Tell us a bit about your wishes...' })}
        ></textarea>
      </div>

      <button
        type="submit"
        disabled={status === 'sending'}
        className={`w-full py-4 rounded-xl font-bold text-white transition-all transform hover:scale-[1.02] active:scale-95 shadow-lg shadow-[#85d7f9]/20 ${
          status === 'sending' ? 'bg-gray-400' : 'bg-[#85d7f9] hover:opacity-90'
        }`}
      >
        {status === 'sending' ? t({ da: 'Sender...', en: 'Sending...' }) : t({ da: 'Send besked', en: 'Send message' })}
      </button>

      {status === 'success' && (
        <div className="p-4 bg-green-50 text-green-700 rounded-xl text-center font-medium">
          {t({ 
            da: 'Tak for din besked! Vi vender tilbage hurtigst muligt.',
            en: 'Thank you for your message! We will get back to you as soon as possible.'
          })}
        </div>
      )}

      {status === 'error' && (
        <div className="p-4 bg-red-50 text-red-700 rounded-xl text-center font-medium">
          {t({ 
            da: 'Der opstod en fejl. Prøv venligst igen eller ring til os.',
            en: 'An error occurred. Please try again or call us.'
          })}
        </div>
      )}
    </form>
  );
}
